import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react';
import { parseDateQuery, entityMatchesDateQuery } from '../utils/dateSearchUtils';
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator, Image, StyleSheet, Platform, Linking, Alert, Modal, TextInput, Pressable, LayoutAnimation, UIManager } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { globalStyles } from '../styles/globalStyles';
import { venueListStyles } from '../styles/venueListStyles';
import { homeStyles } from '../styles/HomeStyles';
import { getResponsiveValue } from '../styles/breakpoints';
import { SearchBar } from '../components/SearchBar';
import { Ionicons } from '@expo/vector-icons';
import { MediaViewer } from '../components/MediaViewer';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import { useUser } from '../components/LoginPersistence';
import { useCommunity } from '../contexts/CommunityContext';
import { CustomModal } from '../components/Modal';
import { CommunityAuthForm } from '../components/CommunityAuthForm';
import { CommunityMemberUpgradeForm } from '../components/CommunityMemberUpgradeForm';
import ProfileTabbedMediaManager from '../components/ProfileTabbedMediaManager';
import { OrganizationCard } from '../components/OrganizationCard';
import { LinearGradient } from 'expo-linear-gradient';
import PostGridDisplay from '../components/PostGridDisplay';
import { CommunityPostCard } from '../components/CommunityPostCard';
import { useInterestCatalog } from '../hooks/useInterestCatalog';
import { resolveInterestDisplayItems } from '../utils/interestUtils';
import { getFirestore, doc, deleteDoc, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { createOrganizationForUser, getMaxOrgsPerUser, getUserOrganizationCount } from '../utils/organizationUtils';

const getFileType = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();

  // Image types
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
    return 'image';
  }

  // PDF
  if (extension === 'pdf') {
    return 'pdf';
  }

  // Audio types
  if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(extension)) {
    return 'audio';
  }

  // Video types
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
    return 'video';
  }

  // Default to file
  return 'file';
};

const orgCardColors = { purpleReplacement: '#166fff' };
const orgCardProfileStyles = StyleSheet.create({
  profileTopLeftCard: {
    width: '100%',
    maxWidth: 760,
    alignSelf: 'center',
    borderRadius: 12,
    padding: 16,
  },
  profileImageContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    overflow: 'hidden',
    alignSelf: 'center',
    position: 'relative',
  },
  profileImage: {
    width: '100%',
    height: '100%',
  },
  profileImageOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.38)',
    opacity: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  profileImageOverlayHovered: {
    opacity: 1,
  },
  profileImageOverlayText: {
    color: '#fff',
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    textAlign: 'center',
    paddingHorizontal: 8,
  },
  profileInput: {
    width: '100%',
    borderRadius: 8,
  },
  characterCount: {
    fontFamily: 'VastagoGrotesk',
  },
  sectionTitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '700',
  },
  signOutButton: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  signOutText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '700',
  },
});

const CommunityList = forwardRef(({ }, ref) => {
  const MAX_ORGS_PER_USER = 3;
  const route = useRoute();
  const navigation = useNavigation();
  const { user: currentUserData, setUser } = useUser();
  const { openMessaging } = useCommunityModal();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCommunityMemberSuccessModal, setShowCommunityMemberSuccessModal] = useState(false);
  const {
    entities,
    allEntities,
    loading: communityLoading,
    hasMore,
    refreshEntities,
    loadMoreEntities,
  } = useCommunity();

  const { interests: interestCatalog } = useInterestCatalog();

  const [displayedEntities, setDisplayedEntities] = useState([]);
  const [allFilteredEntities, setAllFilteredEntities] = useState([]); // Store all filtered results

  const isCurrentUserAdmin = currentUserData?.userType === 'admin';
  // Filter admin-only posts for non-admin users
  const filterAdminOnly = (list) =>
    isCurrentUserAdmin ? list : (list || []).filter((e) => !e.adminOnly);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEnhancedSearch, setIsEnhancedSearch] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
  const [isMediaViewerVisible, setIsMediaViewerVisible] = useState(false);
  const [currentUserMedia, setCurrentUserMedia] = useState(null);
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState(null);
  const [legendVisible, setLegendVisible] = useState(false);
  const [gateModalVisible, setGateModalVisible] = useState(false);
  const [gateModalTitle, setGateModalTitle] = useState('');
  const [gateModalMessage, setGateModalMessage] = useState('');
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [postModalVisible, setPostModalVisible] = useState(false);
  const [postAsModalVisible, setPostAsModalVisible] = useState(false);
  const [postAsMyselfConfirmVisible, setPostAsMyselfConfirmVisible] = useState(false);
  const [showListingLimitModal, setShowListingLimitModal] = useState(false);
  const [postSelected, setPostSelected] = useState({ event: null, crew: null, performer: null, resource: null });
  const [postCustom, setPostCustom] = useState({ event: '', crew: '', performer: '', resource: '' });
  const [postAs, setPostAs] = useState({ type: 'user', organizationId: null, organizationName: null });
  /** Orgs user owns or is a member of; canPostAsOrganization false = show disabled in post-as picker */
  const [postAsOrganizations, setPostAsOrganizations] = useState([]);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [createdOrgForEdit, setCreatedOrgForEdit] = useState(null);
  const [showFolderSelectModal, setShowFolderSelectModal] = useState(false);
  const [postToSave, setPostToSave] = useState(null);
  const [profileToSave, setProfileToSave] = useState(null);
  const [postOptionsOpenId, setPostOptionsOpenId] = useState(null);
  const [deleteConfirmPost, setDeleteConfirmPost] = useState(null);
  const lastScrollY = useRef(0);

  const { theme } = useTheme();

  // Build searchable text from a post/entity for simple text match
  const getSearchableText = useCallback((entity) => {
    const parts = [];
    if (entity.postContent) parts.push(String(entity.postContent));
    if (entity.location) parts.push(String(entity.location));
    if (entity.displayName) parts.push(String(entity.displayName));
    if (entity.username) parts.push(String(entity.username));
    if (entity.firstName) parts.push(String(entity.firstName));
    if (entity.title) parts.push(String(entity.title));
    if (entity.bio) parts.push(String(entity.bio));
    if (Array.isArray(entity.items)) {
      entity.items.forEach((item) => {
        if (item && (item.type === 'text' || item.type === 'link' || item.type === 'date') && item.value) {
          parts.push(String(item.value));
        }
      });
    }
    return parts.join(' ').toLowerCase();
  }, []);

  const performSearch = useCallback((searchTerm) => {
    if (accessBlocked) return;

    const term = (searchTerm || '').trim();
    if (!term) {
      setDisplayedEntities(filterAdminOnly(entities));
      setAllFilteredEntities([]);
      setIsEnhancedSearch(false);
      return;
    }

    if (allEntities.length === 0) {
      setAllFilteredEntities([]);
      setDisplayedEntities([]);
      return;
    }

    setLoading(true);
    const lower = term.toLowerCase();
    const parsedDate = parseDateQuery(term);

    const results = allEntities.filter((entity) => {
      // Date-aware match: if the query looks like a date/range, check calendar widgets
      if (parsedDate && entityMatchesDateQuery(entity, parsedDate)) return true;
      // Fall back to standard substring match across all text fields
      const text = getSearchableText(entity);
      return text.includes(lower);
    });

    setIsEnhancedSearch(false);
    setAllFilteredEntities(results);
    setDisplayedEntities(filterAdminOnly(results).slice(0, 25));
    setLoading(false);
  }, [accessBlocked, entities, allEntities, getSearchableText]);

  // Handle media press to open MediaViewer
  const handleMediaPress = (entity, mediaIndex) => {
    const isOrganization = entity.entityType === 'organization';
    const mediaItems = isOrganization
      ? (entity.media || [])
      : (entity.publicMedia?.media || []);

    if (mediaItems && mediaItems.length > 0) {
      setCurrentUserMedia(mediaItems);
      setSelectedMediaIndex(mediaIndex);
      setIsMediaViewerVisible(true);
    }
  };

  const handleMediaViewerClose = () => {
    setIsMediaViewerVisible(false);
    setSelectedMediaIndex(null);
    setCurrentUserMedia(null);
  };

  // Sync displayed entities with context entities when not searching
  useEffect(() => {
    if (!searchQuery) {
      setDisplayedEntities(filterAdminOnly(entities));
      setAllFilteredEntities([]);
    }
  }, [entities, searchQuery]);

  // Re-run search when allEntities or searchQuery changes (e.g. initial load with query, or new data)
  useEffect(() => {
    if (searchQuery.trim() && allEntities.length > 0) {
      performSearch(searchQuery);
    }
  }, [allEntities, searchQuery, performSearch]);

  // Apply search query from navigation params
  useEffect(() => {
    if (route.params?.searchQuery) {
      setSearchQuery(route.params.searchQuery);
    } else {
      setSearchQuery('');
      setAllFilteredEntities([]);
      setDisplayedEntities(filterAdminOnly(entities));
    }
  }, [route.params?.searchQuery, entities]);

  // Auto-open post flow when navigated here with openPostFlow param
  useEffect(() => {
    if (route.params?.openPostFlow) {
      handleOpenPostFlow();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.params?.openPostFlow, route.params?.timestamp]);

  // Gate: Admin-only access for now
  // useEffect(() => {
  //   const checkAdmin = async () => {
  //     try {
  //       const currentUser = auth.currentUser;
  //       if (!currentUser) {
  //         setGateModalTitle('Coming Soon');
  //         setGateModalMessage('This feature is coming soon.');
  //         setGateModalVisible(true);
  //         setAccessBlocked(true);
  //         setUsers([]);
  //         return;
  //       }
  //       const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
  //       if (!userSnap.exists() || userSnap.data().userType !== 'admin') {
  //         setGateModalTitle('Coming Soon');
  //         setGateModalMessage('This feature is coming soon.');
  //         setGateModalVisible(true);
  //         setAccessBlocked(true);
  //         setUsers([]);
  //         return;
  //       }
  //     } catch (e) {
  //       console.error('Admin gate check failed:', e);
  //     }
  //   };
  //   checkAdmin();
  // }, []);

  const handleSearch = () => {
    performSearch(searchQuery);
  };

  const handleUserPress = (entity) => {
    if (entity.entityType === 'organization') {
      navigation.navigate('OrganizationProfileScreen', { organizationId: entity.id });
    } else {
      navigation.navigate('UserProfileScreen', { userId: entity.id });
    }
  };

  const handlePostAuthorPress = useCallback((entity) => {
    if (entity?.postAsOrganizationId) {
      navigation.navigate('OrganizationProfileScreen', { organizationId: entity.postAsOrganizationId });
      return;
    }
    if (entity?.userId) {
      navigation.navigate('UserProfileScreen', { userId: entity.userId });
    }
  }, [navigation]);

  const getAccountTypeColor = (accountType) => {
    const type = (accountType || '').toLowerCase();
    switch (type) {
      case 'individual':
        return '#2196F3'; // Blue
      case 'business':
        return '#4CAF50'; // Green
      case 'organization':
        return '#FFEB3B'; // Yellow
      case 'band':
        return '#F44336'; // Red
      case 'member':
        return '#2196F3'; // Treat member as blue
      default:
        return '#2196F3'; // Default to blue when unset/unknown
    }
  };

  const handleSendMessage = (entity) => {
    // Check if user is logged in
    if (!currentUserData) {
      // User is not logged in, show auth form
      setShowAuthModal(true);
      return;
    }

    // Check if current user is a community member
    // User is logged in but not a community member, show message
    if (!currentUserData.isCommunityMember) {
      setShowAuthModal(true);
      return;
    }

    // User is logged in and is a community member, proceed
    openMessaging(entity.id, entity.username);
  };

  const handleSendMessageForPost = useCallback((post) => {
    if (!currentUserData) {
      setShowAuthModal(true);
      return;
    }
    if (!currentUserData.isCommunityMember) {
      setShowAuthModal(true);
      return;
    }
    const targetId = post?.postAsOrganizationId || post?.userId || null;
    const targetName =
      post?.postAsOrganizationName ||
      post?.username ||
      post?.firstName ||
      'Community Member';
    if (!targetId) return;
    openMessaging(targetId, targetName);
  }, [currentUserData, openMessaging]);

  const handleOpenPostFlow = useCallback(async () => {
    if (!currentUserData?.uid) {
      setShowAuthModal(true);
      return;
    }
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'communityPosts'),
        where('userId', '==', currentUserData.uid)
      );
      const snap = await getDocs(q);
      if (snap.size >= 3) {
        setShowListingLimitModal(true);
        return;
      }
      setPostAs({ type: 'user', organizationId: null, organizationName: null });
      setPostAsModalVisible(true);
    } catch (err) {
      console.error('Error checking community listing limit:', err);
      Alert.alert('Error', 'Could not start a new post. Please try again.');
    }
  }, [currentUserData?.uid]);

  const loadPostAsOrganizations = useCallback(async () => {
    if (!currentUserData?.uid) {
      setPostAsOrganizations([]);
      setPostAs({ type: 'user', organizationId: null, organizationName: null });
      return;
    }
    const db = getFirestore();
    const uid = currentUserData.uid;
    const byId = new Map();

    try {
      const ownedSnap = await getDocs(
        query(collection(db, 'organizations'), where('ownerId', '==', uid))
      );
      ownedSnap.docs.forEach((d) => {
        const data = d.data();
        byId.set(d.id, {
          id: d.id,
          ...data,
          canPostAsOrganization: true,
          isOrgOwner: true,
        });
      });

      const userSnap = await getDoc(doc(db, 'users', uid));
      const orgLinks = userSnap.exists() ? userSnap.data().organizations || {} : {};
      for (const orgId of Object.keys(orgLinks)) {
        if (byId.has(orgId)) continue;
        const oref = await getDoc(doc(db, 'organizations', orgId));
        if (!oref.exists()) continue;
        const data = oref.data();
        const members = data.members || [];
        if (data.ownerId === uid) {
          byId.set(orgId, {
            id: orgId,
            ...data,
            canPostAsOrganization: true,
            isOrgOwner: true,
          });
          continue;
        }
        if (!members.includes(uid)) continue;
        const allowIds = Array.isArray(data.memberPostAsUserIds) ? data.memberPostAsUserIds : [];
        byId.set(orgId, {
          id: orgId,
          ...data,
          canPostAsOrganization: allowIds.includes(uid),
          isOrgOwner: false,
        });
      }

      setPostAsOrganizations(Array.from(byId.values()));
    } catch (e) {
      console.error('Error loading organizations for post-as picker:', e);
      setPostAsOrganizations([]);
    }
  }, [currentUserData?.uid]);

  // Load organizations user owns or is a member of (for post-as + delete-as-org-owner checks)
  useEffect(() => {
    loadPostAsOrganizations();
  }, [loadPostAsOrganizations]);

  const handleCreateOrganizationFromPostAs = useCallback(async () => {
    if (!currentUserData?.uid) {
      setShowAuthModal(true);
      return;
    }
    if (!currentUserData?.isCommunityMember) {
      setShowAuthModal(true);
      return;
    }

    const db = getFirestore();
    const maxOrgsPerUser = getMaxOrgsPerUser() > 0 ? getMaxOrgsPerUser() : MAX_ORGS_PER_USER;
    if (maxOrgsPerUser > 0) {
      try {
        const count = await getUserOrganizationCount(db, currentUserData.uid);
        if (count >= maxOrgsPerUser) {
          Alert.alert(
            'Organization limit reached',
            `You have reached the maximum number of organizations (${maxOrgsPerUser}). Please leave an organization before creating a new one.`
          );
          return;
        }
      } catch (error) {
        console.error('Error checking organization limit:', error);
        Alert.alert('Error', 'Failed to verify organization limit. Please try again.');
        return;
      }
    }

    setCreatingOrg(true);
    try {
      const org = await createOrganizationForUser(db, currentUserData);
      setCreatedOrgForEdit(org);
      setPostAs({
        type: 'organization',
        organizationId: org.id,
        organizationName: org.title || org.username || 'Organization',
      });
      await loadPostAsOrganizations();
      setShowCreateOrgModal(true);
    } catch (error) {
      console.error('Error creating organization from post modal:', error);
      Alert.alert('Create failed', 'Failed to create organization. Please try again.');
    } finally {
      setCreatingOrg(false);
    }
  }, [MAX_ORGS_PER_USER, currentUserData, loadPostAsOrganizations]);

  const handleSaveCreatedOrg = useCallback(async () => {
    if (!createdOrgForEdit?.id) return null;
    const db = getFirestore();
    const updated = await getDoc(doc(db, 'organizations', createdOrgForEdit.id));
    if (!updated.exists()) return null;
    const data = { id: updated.id, ...updated.data() };
    setCreatedOrgForEdit(data);
    setPostAs({
      type: 'organization',
      organizationId: data.id,
      organizationName: data.title || data.username || 'Organization',
    });
    await loadPostAsOrganizations();
    return data;
  }, [createdOrgForEdit?.id, loadPostAsOrganizations]);

  const handleDeletePost = useCallback((entity) => {
    if (!currentUserData?.uid) return;
    const isOwner = entity.userId === currentUserData.uid;
    const isOrgOwner = entity.postAsOrganizationId &&
      postAsOrganizations.some(
        (org) => org.id === entity.postAsOrganizationId && org.ownerId === currentUserData.uid
      );
    if (!isOwner && !isOrgOwner) return;
    setDeleteConfirmPost(entity);
  }, [currentUserData?.uid, postAsOrganizations]);

  const renderPostCard = (entity) => {
    const isProfileDirectoryListing = entity.entityType === 'profileCard';
    const profileInterestItems = isProfileDirectoryListing
      ? resolveInterestDisplayItems(entity.interests || [], interestCatalog)
      : resolveInterestDisplayItems(entity.interests || [], interestCatalog);
    const showCardInterests = isProfileDirectoryListing
      ? entity.showInterestsOnCard !== false && profileInterestItems.length > 0
      : entity.showInterestsOnPost !== false && profileInterestItems.length > 0;
    const canDelete = entity.entityType === 'post' && !!currentUserData?.uid && (
      entity.userId === currentUserData.uid ||
      (entity.postAsOrganizationId &&
        postAsOrganizations.some(
          (org) => org.id === entity.postAsOrganizationId && org.ownerId === currentUserData.uid
        ))
    );

    return (
      <CommunityPostCard
        key={entity.id}
        post={entity}
        showFolderButton
        showHeadlinePlaceholder={!isProfileDirectoryListing}
        onPress={() => handlePostAuthorPress(entity)}
        onImagePress={(url) => setFullscreenImageUrl(url)}
        showAdminBadge={isCurrentUserAdmin && !!entity.adminOnly}
        onFolderPress={() => {
          if (!currentUserData?.uid) {
            Alert.alert(
              'Sign in',
              isProfileDirectoryListing
                ? 'Please sign in to save profiles to a folder.'
                : 'Please sign in to save posts to a folder.'
            );
            return;
          }
          if (isProfileDirectoryListing) {
            setPostToSave(null);
            setProfileToSave(entity);
          } else {
            setProfileToSave(null);
            setPostToSave(entity);
          }
          setShowFolderSelectModal(true);
        }}
        onMessagePress={() => handleSendMessageForPost(entity)}
        interestItems={profileInterestItems}
        showInterestEmojis={showCardInterests}
        rightActions={(
          <>
              {canDelete && (
                <TouchableOpacity
                  style={styles.postCardOptionsBtn}
                  onPress={(e) => {
                    if (e?.stopPropagation) e.stopPropagation();
                    setPostOptionsOpenId(prev => (prev === entity.id ? null : entity.id));
                  }}
                >
                  <Ionicons name="ellipsis-vertical" size={22} color="#555" />
                </TouchableOpacity>
              )}
              {postOptionsOpenId === entity.id && (
                <View style={styles.postCardOptionsMenu}>
                  <TouchableOpacity
                    style={styles.postCardOptionsItem}
                    onPress={(e) => {
                      if (e?.stopPropagation) e.stopPropagation();
                      setPostOptionsOpenId(null);
                      navigation.navigate('CommunityPostCreate', {
                        editPostId: entity.id,
                        existingPost: entity,
                      });
                    }}
                  >
                    <Text style={styles.postCardOptionsItemText}>Edit</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.postCardOptionsItem}
                    onPress={(e) => {
                      if (e?.stopPropagation) e.stopPropagation();
                      setPostOptionsOpenId(null);
                      handleDeletePost(entity);
                    }}
                  >
                    <Text style={[styles.postCardOptionsItemText, { color: '#e74c3c' }]}>Delete</Text>
                  </TouchableOpacity>
                </View>
              )}
          </>
        )}
      />
    );
  };

  const renderUserCard = (entity) => {
    const isOrganization = entity.entityType === 'organization';
    const truncatedBio = entity.bio && entity.bio.length > 120
      ? entity.bio.substring(0, 120) + '...'
      : entity.bio;

    // Define user type colors similar to Profile.js
    const USER_TYPES = {
      'ARTIST': { color: '#ff6b35', label: 'Artist' },
      'VENUE_OWNER': { color: '#4caf50', label: 'Venue Owner' },
      'PROMOTER': { color: '#9c27b0', label: 'Promoter' },
      'BOOKING_AGENT': { color: '#ff9800', label: 'Booking Agent' },
      'TOUR_MANAGER': { color: '#2196f3', label: 'Tour Manager' },
      'SOUND_ENGINEER': { color: '#607d8b', label: 'Sound Engineer' },
      'LIGHTING_TECHNICIAN': { color: '#ffeb3b', label: 'Lighting Technician' },
      'VENUE_EMPLOYEE': { color: '#795548', label: 'Venue Employee' },
      'FAN': { color: '#e91e63', label: 'Fan' },
      'MEMBER': { color: '#0bb5df', label: 'Member' }
    };

    // Get media - organizations use 'media' array, users use 'publicMedia.media'
    const mediaItems = isOrganization
      ? (entity.media || [])
      : (entity.publicMedia?.media || []);

    if (loading || communityLoading) {
      return (
        <View style={globalStyles.loadingContainer}>
          <ActivityIndicator size="large" color="#fff" />
        </View>
      );
    }


    return (
      <TouchableOpacity
        key={entity.id || entity.username}
        style={styles.userCard}
        onPress={() => handleUserPress(entity)}
      >
        <View style={styles.profileCard}>
          {entity.photoURL ? (
            <Image source={{ uri: entity.photoURL }} style={styles.profileImage} />
          ) : (
            <View style={styles.profileImagePlaceholder}>
              <Text style={styles.profileImagePlaceholderText}>
                {isOrganization
                  ? (entity.username ? entity.username[0].toUpperCase() : 'O')
                  : (entity.firstName ? entity.firstName[0].toUpperCase() :
                    entity.username ? entity.username[0].toUpperCase() : '?')}
              </Text>
            </View>
          )}

          {/* Paper Plane Button - Show for both users and organizations if current user is a community member */}
          <TouchableOpacity
            style={styles.paperPlaneButton}
            onPress={(e) => {
              e.stopPropagation();
              handleSendMessage(entity);
            }}
          >
            <Image
              source={require('../../assets/icons/paper-plane-icon.svg')}
              style={[
                styles.paperPlaneIcon,
                { tintColor: getAccountTypeColor(isOrganization ? 'organization' : entity.accountType) }
              ]}
            />
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.profileFolderButton}
            onPress={(e) => {
              if (e?.stopPropagation) e.stopPropagation();
              if (!currentUserData?.uid) {
                Alert.alert('Sign in', 'Please sign in to save profiles to a folder.');
                return;
              }
              setPostToSave(null);
              setProfileToSave(entity);
              setShowFolderSelectModal(true);
            }}
          >
            <Ionicons name="folder-open" size={24} color="#bca785" />
          </TouchableOpacity>


          <Text style={styles.profileName}>
            {entity.username || (isOrganization ? 'Organization' : 'Anonymous User')}
          </Text>

          {entity.location && (
            <Text style={styles.locationText}>{entity.location}</Text>
          )}

          {entity.title && (
            <Text style={styles.titleText}>{entity.title}</Text>
          )}

          {/* Public Media Display */}
          {mediaItems && mediaItems.length > 0 && (
            <View style={styles.publicMediaContainer}>
              <View style={styles.mediaPreviewGrid}>
                {mediaItems.slice(0, 3).map((item, index) => (
                  <TouchableOpacity
                    key={item.id || index}
                    style={styles.mediaPreviewItem}
                    onPress={() => handleMediaPress(entity, index)}
                  >
                    {getFileType(item.name) === 'image' ? (
                      <Image
                        source={{ uri: item.url }}
                        style={styles.mediaPreviewImage}
                      />
                    ) : getFileType(item.name) === 'pdf' ? (
                      <View style={[styles.mediaPreviewIcon, { backgroundColor: '#e74c3c' }]}>
                        <Ionicons name="document-text" size={24} color="#ffffff" />
                        <Text
                          style={styles.mediaPreviewLabel}
                          numberOfLines={1}
                          ellipsizeMode="middle"
                        >
                          {item.name}
                        </Text>
                      </View>
                    ) : getFileType(item.name) === 'audio' ? (
                      <View style={styles.audioWaveformPreview}>
                        <View style={styles.audioWaveformContainer}>
                          <View style={[styles.audioWaveformBar, { height: 4 }]} />
                          <View style={[styles.audioWaveformBar, { height: 28 }]} />
                          <View style={[styles.audioWaveformBar, { height: 6 }]} />
                          <View style={[styles.audioWaveformBar, { height: 32 }]} />
                          <View style={[styles.audioWaveformBar, { height: 10 }]} />
                        </View>
                        {/* Web-only audio player for playback */}
                        {Platform.OS === 'web' && (
                          <audio src={item.url} controls style={{ width: '100%', marginTop: 6 }} />
                        )}
                      </View>
                    ) : getFileType(item.name) === 'video' ? (
                      <View style={styles.mediaPreviewContainer}>
                        <video
                          src={item.url}
                          style={styles.mediaPreviewImage}
                          muted
                          preload="metadata"
                        />
                        <View style={styles.mediaOverlay}>
                          <Ionicons name="play-circle" size={24} color="#ffffff" />
                        </View>
                        <View style={styles.videoDurationOverlay}>
                          <Text style={styles.durationText}>
                            {item.duration || '0:00'}
                          </Text>
                        </View>
                      </View>
                    ) : (
                      <View style={[styles.mediaPreviewIcon, { backgroundColor: '#34495e' }]}>
                        <Ionicons name="attach" size={16} color="#ffffff" />
                        <Text
                          style={styles.mediaPreviewLabel}
                          numberOfLines={1}
                          ellipsizeMode="middle"
                        >
                          FILE
                        </Text>
                      </View>
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}

          {truncatedBio && (
            <Text style={styles.bioText}>{truncatedBio}</Text>
          )}

          {!isOrganization && entity.socialLinks && entity.socialLinks.length > 0 && (
            <View style={styles.socialLinksContainer}>
              {entity.socialLinks.map((link, index) => {
                const linkLower = (link || '').toLowerCase();
                let label = '🔗 Link';

                if (linkLower.includes('spotify')) label = '🎵 Spotify';
                else if (linkLower.includes('instagram')) label = '📷 Instagram';
                else if (linkLower.includes('facebook')) label = '👍 Facebook';
                else if (linkLower.includes('twitter') || linkLower.includes('x.com')) label = '🐦 Twitter';
                else if (linkLower.includes('youtube')) label = '▶️ YouTube';
                else if (linkLower.includes('soundcloud')) label = '🎧 SoundCloud';
                else if (linkLower.includes('tiktok')) label = '🎬 TikTok';
                else if (linkLower.includes('apple')) label = '🍎 Apple Music';
                else if (linkLower.includes('bandcamp')) label = '🎸 Bandcamp';

                return (
                  <TouchableOpacity
                    key={index}
                    style={styles.socialLinkButton}
                    onPress={() => Linking.openURL(link)}
                  >
                    <Text style={styles.socialLinkButtonText}>{label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          <View style={styles.memberStats}>
            <Text style={styles.memberStat}>
              {isOrganization ? 'created' : 'member'} since: {new Date(entity.createdAt?.toDate?.() || entity.createdAt || Date.now()).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const handleScrollInternal = (event) => {
    const currentScrollY = event.nativeEvent.contentOffset.y;

    // Check if user has scrolled near the bottom to load more entities
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const isNearBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 50;
    if (isNearBottom) {
      if (!searchQuery) {
        // For non-search case, use context's loadMoreEntities
        if (hasMore && !loadingMore) {
          loadMoreEntities();
        }
      } else {
        // For search results, load more from allFilteredEntities
        if (displayedEntities.length < allFilteredEntities.length) {
          const currentLength = displayedEntities.length;
          const nextBatch = allFilteredEntities.slice(currentLength, currentLength + 25);
          setDisplayedEntities(prev => [...prev, ...nextBatch]);
        }
      }
    }

    lastScrollY.current = currentScrollY;
  };

  useImperativeHandle(ref, () => ({
    handleScrollInternal
  }));

  return (
    <View style={[globalStyles.container, { backgroundColor: '#3D7FE6' }]}>

      <View style={[globalStyles.overlay, { backgroundColor: 'transparent', paddingTop: 0 }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 60, marginBottom: 8 }}>
          <TouchableOpacity
            onPress={() => navigation.navigate('CommunityList', route.params ? { ...route.params } : undefined)}
            activeOpacity={0.8}
            style={Platform.OS === 'web' ? { cursor: 'pointer' } : {}}
          >
            <Text
              style={[
                homeStyles.homeSubtitle2,
                { color: '#fbcd9d' },
                { textDecorationLine: 'underline' },
              ]}
            >
              Directory
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => navigation.navigate('EventsList', route.params ? { searchQuery: route.params.searchQuery } : undefined)}
            activeOpacity={0.8}
            style={Platform.OS === 'web' ? { cursor: 'pointer' } : {}}
          >
            <Text
              style={[
                homeStyles.homeSubtitle2,
                { color: '#fbcd9d' },
              ]}
            >
              Events
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => navigation.navigate('GearSwapList', route.params ? { searchQuery: route.params.searchQuery } : undefined)}
            activeOpacity={0.8}
            style={Platform.OS === 'web' ? { cursor: 'pointer' } : {}}
          >
            <Text
              style={[
                homeStyles.homeSubtitle2,
                { color: '#fbcd9d' },
              ]}
            >
              Gear Swap
            </Text>
          </TouchableOpacity>
        </View>
        <View style={[venueListStyles.searchContainer, { borderBottomColor: 'transparent', borderOpacity: 0.5, backgroundColor: 'transparent' }]}>
          <SearchBar
            nativeID="community-directory-search"
            color="#fbcd9d"
            placeholderColor="rgba(251, 205, 157, 0.75)"
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmit={handleSearch}
            placeholder="Search posts..."
          />
        </View>


        <View style={styles.listSection}>
          <View style={venueListStyles.venueListHeaderSection}>
            <View style={{ flex: 1 }} />
            <TouchableOpacity
              style={[venueListStyles.postButtonContainer, { flex: 1 }]}
              onPress={handleOpenPostFlow}
              activeOpacity={0.8}
            >
              <Text style={venueListStyles.postButton}>POST</Text>
            </TouchableOpacity>
            <View style={[venueListStyles.venueCountContainer, { flex: 1 }]}>
              <Text style={[venueListStyles.venueCountText, { color: '#fbcd9d' }]}>
                {searchQuery ? (
                  allFilteredEntities.length > 0
                    ? `${displayedEntities.length} of ${allFilteredEntities.length} ${allFilteredEntities.length === 1 ? 'result' : 'results'} shown`
                    : `${displayedEntities.length} ${displayedEntities.length === 1 ? 'result' : 'results'} found`
                ) : (
                  `${displayedEntities.length} ${displayedEntities.length === 1 ? 'result' : 'results'} found`
                )}
              </Text>
              {/* <TouchableOpacity
                style={[styles.seeLegendButton, { borderColor: 'rgba(251, 205, 157, 0.4)' }]}
                onPress={() => setLegendVisible(true)}
              >
                <Text style={[styles.seeLegendText, { color: '#fbcd9d' }]}>See Legend</Text>
              </TouchableOpacity> */}
            </View>
          </View>
          {accessBlocked ? (
            <View style={styles.loadingContainer}>
              {/* Gate modal will show */}
            </View>
          ) : loading || communityLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#3D7FE6" />
              <Text style={styles.loadingText}>Loading posts...</Text>
            </View>
          ) : displayedEntities.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>No results found</Text>
              <Text style={styles.emptySubtext}>
                {searchQuery ? 'Try a different search term' : 'No posts yet. Create one!'}
              </Text>
            </View>
          ) : (
            <>


              <View style={styles.communityGrid}>
                {displayedEntities.map(renderPostCard)}
              </View>

              {/* Show loading indicator when there are more items to load (matching VenueList pattern) */}
              {searchQuery && displayedEntities.length < allFilteredEntities.length && (
                <View style={styles.loadingMoreContainer}>
                  <ActivityIndicator size="small" color="#4e7de0" />
                  <Text style={styles.loadingMoreText}>Loading more...</Text>
                </View>
              )}


              {!searchQuery && !hasMore && displayedEntities.length > 0 && (
                <View style={styles.endContainer}>
                  <Text style={styles.endText}>You've reached the end!</Text>
                </View>
              )}

              {searchQuery && displayedEntities.length >= allFilteredEntities.length && allFilteredEntities.length > 0 && (
                <View style={styles.endContainer}>
                  <Text style={styles.endText}>You've reached the end!</Text>
                </View>
              )}
            </>
          )}
        </View>

        {/* MediaViewer Modal */}
        {isMediaViewerVisible && (
          <MediaViewer
            visible={isMediaViewerVisible}
            onClose={handleMediaViewerClose}
            media={currentUserMedia}
            currentIndex={selectedMediaIndex}
            onNavigate={setSelectedMediaIndex}
          />
        )}

        {/* Fullscreen image viewer for post grid photos */}
        {!!fullscreenImageUrl && Platform.OS === 'web' && (
          <div
            onClick={() => setFullscreenImageUrl(null)}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.92)', zIndex: 9999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <img
              src={fullscreenImageUrl}
              alt="Fullscreen"
              style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 8 }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setFullscreenImageUrl(null)}
              style={{
                position: 'absolute', top: 20, right: 24,
                background: 'rgba(255,255,255,0.18)', border: 'none', borderRadius: 20,
                color: '#fff', fontSize: 28, cursor: 'pointer', width: 40, height: 40,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >✕</button>
          </div>
        )}

        {/* Access Gate Popup */}
        <Modal
          visible={gateModalVisible}
          transparent={true}
          animationType="fade"
          onRequestClose={() => {
            setGateModalVisible(false);
            navigation.goBack();
          }}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.legendModal}>
              <View style={styles.legendHeader}>
                <Text style={styles.legendModalTitle}>{gateModalTitle}</Text>
                <TouchableOpacity
                  style={styles.closeButton}
                  onPress={() => {
                    setGateModalVisible(false);
                    navigation.goBack();
                  }}
                >
                  <Text style={styles.closeButtonText}>×</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.legendModalContent}>
                <Text style={styles.legendModalText}>{gateModalMessage}</Text>
                <View style={{ marginTop: 16, alignItems: 'center' }}>
                  <TouchableOpacity
                    style={[styles.seeLegendButton, { backgroundColor: 'rgba(255, 255, 255, 0.25)' }]}
                    onPress={() => {
                      setGateModalVisible(false);
                      navigation.goBack();
                    }}
                  >
                    <Text style={styles.seeLegendText}>OK</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </View>
        </Modal>

        {/* Delete listing confirmation Modal */}
        <Modal
          visible={!!deleteConfirmPost}
          transparent
          animationType="fade"
          onRequestClose={() => setDeleteConfirmPost(null)}
        >
          <Pressable style={styles.postModalOverlay} onPress={() => setDeleteConfirmPost(null)}>
            <Pressable style={styles.postModalContent} onPress={(e) => e.stopPropagation()}>
              <Text style={styles.postModalTitle}>Delete listing</Text>
              <Text style={styles.postModalSubtitle}>
                Are you sure you want to delete this listing?
              </Text>
              <View style={styles.postModalFooter}>
                <TouchableOpacity
                  onPress={() => setDeleteConfirmPost(null)}
                  style={[styles.postModalContinueBtn]}
                >
                  <Text style={[styles.postModalContinueText, { color: '#333' }]}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={async () => {
                    const entity = deleteConfirmPost;
                    if (!entity) return;
                    const idToRemove = entity.id;
                    setDeleteConfirmPost(null);
                    if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
                      UIManager.setLayoutAnimationEnabledExperimental(true);
                    }
                    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
                    setDisplayedEntities((prev) => prev.filter((e) => e.id !== idToRemove));
                    setAllFilteredEntities((prev) => prev.filter((e) => e.id !== idToRemove));
                    try {
                      const db = getFirestore();
                      await deleteDoc(doc(db, 'communityPosts', idToRemove));
                      refreshEntities?.();
                    } catch (err) {
                      console.error('Error deleting community post:', err);
                      refreshEntities?.();
                      Alert.alert('Delete failed', 'The listing could not be deleted. The list will refresh.');
                    }
                  }}
                  style={styles.postModalContinueBtn}
                >
                  <Text style={[styles.postModalContinueText, { color: '#e74c3c' }]}>Delete</Text>
                </TouchableOpacity>
              </View>
            </Pressable>
          </Pressable>
        </Modal>

        {/* Legend Popup Modal */}
        <Modal
          visible={legendVisible}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setLegendVisible(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.legendModal}>
              <View style={styles.legendHeader}>
                <Text style={styles.legendModalTitle}>Account Types</Text>
                <TouchableOpacity
                  style={styles.closeButton}
                  onPress={() => setLegendVisible(false)}
                >
                  <Text style={styles.closeButtonText}>×</Text>
                </TouchableOpacity>
              </View>

              <View style={styles.legendModalContent}>
                <View style={styles.legendModalItems}>
                  <View style={styles.legendModalItem}>
                    <View style={[styles.legendIconContainer, { backgroundColor: '#2196F3' }]}>
                      <Image
                        source={require('../../assets/icons/paper-plane-icon.svg')}
                        style={[styles.legendIcon, { tintColor: '#ffffff' }]}
                      />
                    </View>
                    <Text style={styles.legendModalText}>Individual</Text>
                  </View>

                  <View style={styles.legendModalItem}>
                    <View style={[styles.legendIconContainer, { backgroundColor: '#4CAF50' }]}>
                      <Image
                        source={require('../../assets/icons/paper-plane-icon.svg')}
                        style={[styles.legendIcon, { tintColor: '#ffffff' }]}
                      />
                    </View>
                    <Text style={styles.legendModalText}>Business</Text>
                  </View>

                  <View style={styles.legendModalItem}>
                    <View style={[styles.legendIconContainer, { backgroundColor: '#FFEB3B' }]}>
                      <Image
                        source={require('../../assets/icons/paper-plane-icon.svg')}
                        style={[styles.legendIcon, { tintColor: '#ffffff' }]}
                      />
                    </View>
                    <Text style={styles.legendModalText}>Organization</Text>
                  </View>

                  <View style={styles.legendModalItem}>
                    <View style={[styles.legendIconContainer, { backgroundColor: '#F44336' }]}>
                      <Image
                        source={require('../../assets/icons/paper-plane-icon.svg')}
                        style={[styles.legendIcon, { tintColor: '#ffffff' }]}
                      />
                    </View>
                    <Text style={styles.legendModalText}>Band</Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </Modal>

        {/* Listing limit Modal (user already has 3 community listings) */}
        <Modal
          visible={showListingLimitModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowListingLimitModal(false)}
        >
          <Pressable style={styles.postModalOverlay} onPress={() => setShowListingLimitModal(false)}>
            <Pressable style={styles.postModalContent} onPress={(e) => e.stopPropagation()}>
              <Text style={styles.postModalTitle}>Listing limit reached</Text>
              <Text style={styles.postModalSubtitle}>
                You can have up to 3 community listings at a time. Please delete one before creating a new listing.
              </Text>
              <View style={styles.postModalFooter}>
                <TouchableOpacity
                  onPress={() => setShowListingLimitModal(false)}
                  style={styles.postModalContinueBtn}
                >
                  <Text style={styles.postModalContinueText}>OK</Text>
                </TouchableOpacity>
              </View>
            </Pressable>
          </Pressable>
        </Modal>

        {/* \"Post as\" identity Modal (Myself vs Organization) */}
        <Modal
          visible={postAsModalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setPostAsModalVisible(false)}
        >
          <Pressable style={styles.postModalOverlay} onPress={() => setPostAsModalVisible(false)}>
            <Pressable style={styles.postModalContent} onPress={(e) => e.stopPropagation()}>
              <Text style={styles.postModalTitle}>POST</Text>
              <Text style={[styles.postModalSubtitle, styles.postModalCategoryFont]}>WHO ARE YOU POSTING AS?</Text>
              <View style={styles.postAsRow}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.postAsChipsRow}
                >
                  <TouchableOpacity
                    style={[
                      styles.postAsChip,
                      styles.postAsPersonaCard,
                      postAs.type === 'user' && styles.postAsChipSelected,
                    ]}
                    onPress={() => setPostAsMyselfConfirmVisible(true)}
                  >
                    <View style={styles.postAsChipAvatarWrap}>
                      {currentUserData?.photoURL ? (
                        <Image
                          source={{ uri: currentUserData.photoURL }}
                          style={styles.postAsChipAvatarImage}
                          resizeMode="cover"
                        />
                      ) : (
                        <View style={[styles.postAsChipAvatarImage, styles.postAsChipAvatarPlaceholder]}>
                          <Text style={styles.postAsChipAvatarInitial}>
                            {(currentUserData?.username || currentUserData?.firstName || '?')[0].toUpperCase()}
                          </Text>
                        </View>
                      )}
                    </View>
                    <View style={styles.postAsChipLabelBlock}>
                      <Text
                        style={[
                          styles.postAsChipText,
                          postAs.type === 'user' && styles.postAsChipTextSelected,
                        ]}
                        numberOfLines={2}
                      >
                        Myself
                      </Text>
                    </View>
                    <View style={styles.postAsChipFooter}>
                      <View style={styles.postAsChipFooterSpacer} />
                    </View>
                  </TouchableOpacity>
                  {postAsOrganizations.map((org) => {
                    const canPost = org.canPostAsOrganization !== false;
                    const label = org.title || org.username || 'Organization';
                    const selected =
                      postAs.type === 'organization' && postAs.organizationId === org.id;
                    return (
                      <TouchableOpacity
                        key={org.id}
                        style={[
                          styles.postAsChip,
                          styles.postAsPersonaCard,
                          selected && styles.postAsChipSelected,
                          !canPost && styles.postAsChipDisabled,
                        ]}
                        onPress={() => {
                          if (!canPost) {
                            Alert.alert(
                              'Posting not enabled',
                              'Only the organization owner can allow members to post as this organization. Ask them to grant permission in the organization settings.'
                            );
                            return;
                          }
                          setPostAs({
                            type: 'organization',
                            organizationId: org.id,
                            organizationName: label,
                          });
                        }}
                        activeOpacity={canPost ? 0.7 : 1}
                      >
                        <View style={styles.postAsChipAvatarWrap}>
                          {org.photoURL ? (
                            <Image
                              source={{ uri: org.photoURL }}
                              style={styles.postAsChipAvatarImage}
                              resizeMode="cover"
                            />
                          ) : (
                            <View style={[styles.postAsChipAvatarImage, styles.postAsChipAvatarPlaceholder]}>
                              <Text style={styles.postAsChipAvatarInitial}>
                                {(org.username || label)[0].toUpperCase()}
                              </Text>
                            </View>
                          )}
                        </View>
                        <View style={styles.postAsChipLabelBlock}>
                          <Text
                            style={[
                              styles.postAsChipText,
                              selected && styles.postAsChipTextSelected,
                            ]}
                            numberOfLines={2}
                          >
                            {label}
                          </Text>
                        </View>
                        <View style={styles.postAsChipFooter}>
                          {!canPost ? (
                            <Text style={styles.postAsChipLockedLabel} numberOfLines={1}>
                              No post access
                            </Text>
                          ) : (
                            <View style={styles.postAsChipFooterSpacer} />
                          )}
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </View>
              <View style={styles.postAsCreateRow}>
                <TouchableOpacity
                  onPress={handleCreateOrganizationFromPostAs}
                  style={[
                    styles.postAsCreateBtn,
                    (creatingOrg || postAsOrganizations.length >= MAX_ORGS_PER_USER) && { opacity: 0.5 },
                  ]}
                  disabled={creatingOrg || postAsOrganizations.length >= MAX_ORGS_PER_USER}
                >
                  {creatingOrg ? (
                    <ActivityIndicator size="small" color="#166fff" />
                  ) : postAsOrganizations.length >= MAX_ORGS_PER_USER ? (
                    <>
                      <Ionicons name="ban-outline" size={18} color="#166fff" />
                      <Text style={styles.postAsCreateBtnText}>Max organizations reached (3)</Text>
                    </>
                  ) : (
                    <>
                      <Ionicons name="add-circle-outline" size={18} color="#166fff" />
                      <Text style={styles.postAsCreateBtnText}>Create organization</Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
              <View style={styles.postModalFooter}>
                <TouchableOpacity
                  onPress={() => setPostAsModalVisible(false)}
                  style={[styles.postModalContinueBtn]}
                >
                  <Text style={[styles.postModalContinueText, styles.postModalContinuePostFont, { color: '#333' }]}>CANCEL</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => {
                    if (postAs.type === 'organization' && postAs.organizationId) {
                      const o = postAsOrganizations.find((x) => x.id === postAs.organizationId);
                      if (!o || o.canPostAsOrganization === false) {
                        Alert.alert(
                          'Cannot continue',
                          'Pick an organization you are allowed to post as, or choose Myself.'
                        );
                        return;
                      }
                    }
                    setPostAsModalVisible(false);
                    setPostModalVisible(true);
                  }}
                  style={styles.postModalContinueBtn}
                >
                  <Text style={[styles.postModalContinueText, styles.postModalContinuePostFont]}>CONTINUE</Text>
                </TouchableOpacity>
              </View>
            </Pressable>
          </Pressable>
        </Modal>

        <Modal
          visible={showCreateOrgModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowCreateOrgModal(false)}
        >
          <Pressable style={styles.postModalOverlay} onPress={() => setShowCreateOrgModal(false)}>
            <Pressable style={[styles.postModalContent, styles.postAsOrgEditorModal]} onPress={(e) => e.stopPropagation()}>
              <View style={styles.postAsOrgEditorHeader}>
                <Text style={styles.postModalTitle}>Organization</Text>
                <TouchableOpacity onPress={() => setShowCreateOrgModal(false)} style={styles.postAsOrgEditorCloseBtn}>
                  <Ionicons name="close" size={22} color="#166fff" />
                </TouchableOpacity>
              </View>
              <Text style={styles.postModalSubtitle}>Complete your organization profile, then proceed.</Text>
              <ScrollView style={styles.postAsOrgEditorScroll}>
                {createdOrgForEdit?.id ? (
                  <OrganizationCard
                    orgId={createdOrgForEdit.id}
                    orgData={createdOrgForEdit}
                    colors={orgCardColors}
                    profileStyles={orgCardProfileStyles}
                    onSave={handleSaveCreatedOrg}
                    onClose={null}
                    onInvite={handleSaveCreatedOrg}
                    isEditable
                  />
                ) : (
                  <Text style={styles.postAsOrgEditorEmptyText}>
                    Create an organization first from the post-as popup.
                  </Text>
                )}
              </ScrollView>
              <View style={styles.postModalFooter}>
                <TouchableOpacity
                  onPress={() => setShowCreateOrgModal(false)}
                  style={styles.postModalContinueBtn}
                >
                  <Text style={[styles.postModalContinueText, { color: '#333' }]}>CANCEL</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={async () => {
                    const latestOrgData = await handleSaveCreatedOrg();
                    const username = (latestOrgData?.username || '').trim();
                    const title = (latestOrgData?.title || '').trim();
                    if (!username || !title) {
                      Alert.alert(
                        'Finish organization info',
                        'Please complete Organization Username and Organization Title before proceeding.'
                      );
                      return;
                    }
                    setShowCreateOrgModal(false);
                    setPostAsModalVisible(false);
                    setPostModalVisible(true);
                  }}
                  style={styles.postModalContinueBtn}
                >
                  <Text style={styles.postModalContinueText}>PROCEED</Text>
                </TouchableOpacity>
              </View>
            </Pressable>
          </Pressable>
        </Modal>

        {/* POST to Directory Modal (title/categories) */}
        <Modal
          visible={postModalVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setPostModalVisible(false)}
        >
          <Pressable style={styles.postModalOverlay} onPress={() => setPostModalVisible(false)}>
            <Pressable style={[styles.postModalContent, styles.postModalContentDirectory]} onPress={(e) => e.stopPropagation()}>
              <Text style={styles.postModalTitle}>POST</Text>
              <Text style={[styles.postModalSubtitle, styles.postModalCategoryFont]}>TO DIRECTORY AS:</Text>
              <ScrollView
                style={styles.postModalDirectoryScroll}
                contentContainerStyle={styles.postModalDirectoryScrollContent}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
              <View style={styles.postModalColumns}>
                <View style={styles.postModalColumn}>
                  <Text style={styles.postModalColumnHeader}>EVENT</Text>
                  <Pressable onPress={() => setPostSelected(s => ({ event: 'Venue' }))}>
                    <Text style={[styles.postModalOption, postSelected.event === 'Venue' && styles.postModalOptionSelected]}>Venue</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ event: 'Booker' }))}>
                    <Text style={[styles.postModalOption, postSelected.event === 'Booker' && styles.postModalOptionSelected]}>Booker</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ event: 'Promoter' }))}>
                    <Text style={[styles.postModalOption, postSelected.event === 'Promoter' && styles.postModalOptionSelected]}>Promoter</Text>
                  </Pressable>
                  <TextInput
                    placeholder=""
                    value={postCustom.event}
                    onChangeText={(t) => setPostCustom({ event: t })}
                    style={[styles.postModalTextInput, postCustom.event ? styles.postModalTextInputFilled : styles.postModalTextInputEmpty]}
                    placeholderTextColor="#999"
                  />
                </View>
                <View style={styles.postModalColumn}>
                  <Text style={styles.postModalColumnHeader}>CREW</Text>
                  <Pressable onPress={() => setPostSelected(s => ({ crew: 'Driver' }))}>
                    <Text style={[styles.postModalOption, postSelected.crew === 'Driver' && styles.postModalOptionSelected]}>Driver</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ crew: 'Tour Manager' }))}>
                    <Text style={[styles.postModalOption, postSelected.crew === 'Tour Manager' && styles.postModalOptionSelected]}>Tour Manager</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ crew: 'Guitar Tech' }))}>
                    <Text style={[styles.postModalOption, postSelected.crew === 'Guitar Tech' && styles.postModalOptionSelected]}>Guitar Tech</Text>
                  </Pressable>
                  <TextInput
                    placeholder=""
                    value={postCustom.crew}
                    onChangeText={(t) => setPostCustom({ crew: t })}
                    style={[styles.postModalTextInput, postCustom.crew ? styles.postModalTextInputFilled : styles.postModalTextInputEmpty]}
                    placeholderTextColor="#999"
                  />
                </View>
                <View style={styles.postModalColumn}>
                  <Text style={styles.postModalColumnHeader}>PERFORMER</Text>
                  <Pressable onPress={() => setPostSelected(s => ({ performer: 'Band' }))}>
                    <Text style={[styles.postModalOption, postSelected.performer === 'Band' && styles.postModalOptionSelected]}>Band</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ performer: 'Comedian' }))}>
                    <Text style={[styles.postModalOption, postSelected.performer === 'Comedian' && styles.postModalOptionSelected]}>Comedian</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ performer: 'Electronic' }))}>
                    <Text style={[styles.postModalOption, postSelected.performer === 'Electronic' && styles.postModalOptionSelected]}>Electronic</Text>
                  </Pressable>
                  <TextInput
                    placeholder=""
                    value={postCustom.performer}
                    onChangeText={(t) => setPostCustom(c => ({ ...c, performer: t }))}
                    style={[styles.postModalTextInput, postCustom.performer ? styles.postModalTextInputFilled : styles.postModalTextInputEmpty]}
                    placeholderTextColor="#999"
                  />
                </View>
                <View style={styles.postModalColumn}>
                  <Text style={styles.postModalColumnHeader}>RESOURCE</Text>
                  <Pressable onPress={() => setPostSelected(s => ({ resource: 'Backline Rental' }))}>
                    <Text style={[styles.postModalOption, postSelected.resource === 'Backline Rental' && styles.postModalOptionSelected]}>Backline Rental</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ resource: 'Rehearsal Space' }))}>
                    <Text style={[styles.postModalOption, postSelected.resource === 'Rehearsal Space' && styles.postModalOptionSelected]}>Rehearsal Space</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ resource: 'Repair Shop' }))}>
                    <Text style={[styles.postModalOption, postSelected.resource === 'Repair Shop' && styles.postModalOptionSelected]}>Repair Shop</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ resource: 'Graphic Design' }))}>
                    <Text style={[styles.postModalOption, postSelected.resource === 'Graphic Design' && styles.postModalOptionSelected]}>Graphic Design</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ resource: 'Screen Printing' }))}>
                    <Text style={[styles.postModalOption, postSelected.resource === 'Screen Printing' && styles.postModalOptionSelected]}>Screen Printing</Text>
                  </Pressable>
                  <Pressable onPress={() => setPostSelected(s => ({ resource: 'Cassette Duplication' }))}>
                    <Text style={[styles.postModalOption, postSelected.resource === 'Cassette Duplication' && styles.postModalOptionSelected]}>Cassette Duplication</Text>
                  </Pressable>
                  <TextInput
                    placeholder=""
                    value={postCustom.resource}
                    onChangeText={(t) => setPostCustom({ resource: t })}
                    style={[styles.postModalTextInput, postCustom.resource ? styles.postModalTextInputFilled : styles.postModalTextInputEmpty]}
                    placeholderTextColor="#999"
                  />
                </View>
              </View>
              </ScrollView>
              <View style={styles.postModalFooter}>
                <TouchableOpacity
                  onPress={() => {
                    setPostModalVisible(false);
                    navigation.navigate('CommunityPostCreate', {
                      postSelected: { ...postSelected },
                      postCustom: { ...postCustom },
                      postAs,
                    });
                  }}
                  style={styles.postModalContinueBtn}
                >
                  <Text style={[styles.postModalContinueText, styles.postModalContinuePostFont]}>CONTINUE</Text>
                </TouchableOpacity>
              </View>
            </Pressable>
          </Pressable>
        </Modal>

        {/* "Post as myself" confirmation prompt */}
        <Modal
          visible={postAsMyselfConfirmVisible}
          transparent
          animationType="fade"
          onRequestClose={() => setPostAsMyselfConfirmVisible(false)}
        >
          <Pressable
            style={[styles.postModalOverlay, { justifyContent: 'center', padding: 24 }]}
            onPress={() => setPostAsMyselfConfirmVisible(false)}
          >
            <Pressable style={[styles.postModalContent, { maxWidth: 400, alignSelf: 'center', width: '100%' }]} onPress={(e) => e.stopPropagation()}>
              <Text style={styles.postModalTitle}>POST AS YOURSELF</Text>
              <Text style={[styles.postModalSubtitle, { marginBottom: 20, textAlign: 'center', lineHeight: 20 }]}>
                Do you want to edit your profile card (in your account page) or post something else?
              </Text>
              <View style={{ gap: 12 }}>
                <TouchableOpacity
                  style={[styles.postModalContinueBtn, { backgroundColor: '#166fff' }]}
                  onPress={() => {
                    setPostAsMyselfConfirmVisible(false);
                    setPostAsModalVisible(false);
                    navigation.navigate('Profile');
                  }}
                >
                  <Text style={[styles.postModalContinueText, { color: '#fff' }]}>Edit My Profile Card</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.postModalContinueBtn, { backgroundColor: '#3ba378' }]}
                  onPress={() => {
                    setPostAs({ type: 'user', organizationId: null, organizationName: null });
                    setPostAsMyselfConfirmVisible(false);
                  }}
                >
                  <Text style={[styles.postModalContinueText, { color: '#fff' }]}>Post Something</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{ paddingVertical: 10, alignItems: 'center' }}
                  onPress={() => setPostAsMyselfConfirmVisible(false)}
                >
                  <Text style={{ color: '#999', fontSize: 14 }}>Cancel</Text>
                </TouchableOpacity>
              </View>
            </Pressable>
          </Pressable>
        </Modal>

        {/* Save post to folder modal */}
        <Modal
          visible={showFolderSelectModal && (!!postToSave || !!profileToSave)}
          transparent
          animationType="fade"
          onRequestClose={() => {
            setShowFolderSelectModal(false);
            setPostToSave(null);
            setProfileToSave(null);
          }}
        >
          <View style={styles.modalOverlay}>
            <View style={[styles.folderSelectModalContent, { backgroundColor: theme.background.primary }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: 'rgba(0,0,0,0.08)' }}>
                <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 18, fontWeight: 'bold', color: theme.text.primary }}>
                  {profileToSave ? 'Select folder to save profile' : 'Select folder to save post'}
                </Text>
                <TouchableOpacity onPress={() => {
                  setShowFolderSelectModal(false);
                  setPostToSave(null);
                  setProfileToSave(null);
                }} style={{ padding: 8 }}>
                  <Ionicons name="close" size={24} color="#1d1d1d" />
                </TouchableOpacity>
              </View>
              <View style={{ flex: 1 }}>
                {currentUserData && (
                  <ProfileTabbedMediaManager
                    libraryTab="community"
                    userData={currentUserData}
                    setUser={setUser}
                    onEditReport={() => {}}
                    onDeleteReport={() => {}}
                    onViewVenue={() => {}}
                    onAddVenuePhoto={() => {}}
                    onDeleteVenuePhoto={() => {}}
                    onFolderSelect={() => {
                      setShowFolderSelectModal(false);
                      setPostToSave(null);
                      setProfileToSave(null);
                    }}
                    communityPostToSave={postToSave}
                    communityProfileToSave={profileToSave}
                    allowFileUpload={false}
                    allowProfileDropImport={false}
                  />
                )}
              </View>
            </View>
          </View>
        </Modal>

        {/* Auth/Community Member Modal */}
        {!currentUserData ? (
          // User is not logged in, show auth form
          <Modal visible={showAuthModal && !currentUserData} transparent={true} animationType="fade" onRequestClose={() => setShowAuthModal(false)}>
            <View style={styles.authModalOverlay}>
              <CommunityAuthForm isModal={true} onClose={() => setShowAuthModal(false)} />
            </View>
          </Modal>
        ) : (
          // User is logged in but not a community member, show upgrade form
          <Modal visible={showAuthModal && currentUserData && !currentUserData.isCommunityMember} transparent={true} animationType="fade" onRequestClose={() => setShowAuthModal(false)}>
            <View style={styles.authModalOverlay}>
              <CommunityMemberUpgradeForm
                isModal={true}
                onClose={() => setShowAuthModal(false)}
                message="You must be a community member to send messages."
                onSuccess={() => {
                  setShowAuthModal(false);
                  setShowCommunityMemberSuccessModal(true);
                }}
                onError={(error) => {
                  console.error('Error upgrading to community member:', error);
                }}
              />
            </View>
          </Modal>
        )}

        {/* Community Member Success Modal */}
        <Modal
          visible={showCommunityMemberSuccessModal}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setShowCommunityMemberSuccessModal(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={{
              backgroundColor: '#ffffff',
              borderRadius: 12,
              padding: 20,
              width: '100%',
              maxWidth: 400,
              alignItems: 'center',
            }}>
              <Ionicons name="checkmark-circle" size={48} color="#2ecc71" />
              <Text style={{
                fontSize: 18,
                fontFamily: 'Glacial Indifference',
                marginTop: 10,
                marginBottom: 15,
                textAlign: 'center',
                color: '#2ecc71',
              }}>Congratulations!</Text>

              <Text style={{
                fontSize: 14,
                color: '#333',
                textAlign: 'center',
                marginBottom: 20,
                lineHeight: 20,
              }}>You are now a community member.</Text>

              <TouchableOpacity
                onPress={() => setShowCommunityMemberSuccessModal(false)}
                style={{
                  backgroundColor: '#2ecc71',
                  borderRadius: 8,
                  paddingHorizontal: 30,
                  paddingVertical: 12,
                  alignItems: 'center',
                }}
              >
                <Text style={{
                  color: '#ffffff',
                  fontSize: 16,
                  fontFamily: 'Glacial Indifference',
                  fontWeight: 'bold',
                }}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 0.4,
    },
    '50%': {
      opacity: 1,
    },
  },
  /** Fills width but sizes to card content so the screen ScrollView can scroll the full list */
  listSection: {
    width: '100%',
    alignSelf: 'stretch',
  },
  communityGrid: {
    width: '100%',
    maxWidth: 1400,
    alignSelf: 'center',
    marginTop: 35,
    display: Platform.OS === 'web' ? 'grid' : 'flex',
    ...(Platform.OS === 'web' && {
      gridTemplateColumns: getResponsiveValue({
        mobile: 'repeat(1, 1fr)',
        tablet: 'repeat(2, 1fr)',
        laptop: 'repeat(3, 1fr)',
        desktop: 'repeat(3, 1fr)'
      }),
      gap: getResponsiveValue({
        mobile: 15,
        tablet: 15,
        laptop: 25,
        desktop: 30
      }),
      alignItems: 'center',
      justifyItems: getResponsiveValue({
        mobile: 'stretch',
        tablet: 'stretch',
        laptop: 'stretch',
        desktop: 'stretch'
      }),
      paddingHorizontal: getResponsiveValue({
        mobile: 12,
        tablet: 20,
        laptop: 20,
        desktop: 20
      }),
      marginBottom: getResponsiveValue({
        mobile: 20,
        tablet: 30,
        laptop: 40,
        desktop: 40
      }),
    }),
    // Mobile flexbox fallback
    ...(Platform.OS !== 'web' && {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingBottom: 20,
    }),
  },
  userCard: {
    backgroundColor: '#feffe4',
    borderRadius: 12,
    height: getResponsiveValue({
      mobile: 380,
      tablet: 440,
      laptop: 480,
      desktop: 500
    }),
    position: 'relative',
    overflow: 'hidden',
    width: getResponsiveValue({
      mobile: '90vw',
      tablet: '100%',
      laptop: '100%',
      desktop: '100%'
    }),
    marginLeft: getResponsiveValue({
      mobile: 'auto',
      tablet: 0,
      laptop: 0,
      desktop: 0
    }),
    marginRight: getResponsiveValue({
      mobile: 'auto',
      tablet: 0,
      laptop: 0,
      desktop: 0
    }),
    marginBottom: Platform.OS !== 'web' ? 16 : 0,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
      ':hover': {
        transform: 'scale(1.02)',
        boxShadow: '0 12px 20px rgba(0,0,0,0.3)',
      },
    }),
  },
  profileCard: {
    height: '100%',
    width: '100%',
    paddingTop: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 25,
      desktop: 30
    }),
    paddingHorizontal: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 25,
      desktop: 30
    }),
    paddingBottom: getResponsiveValue({
      mobile: 35,
      tablet: 40,
      laptop: 45,
      desktop: 50
    }),
    gap: getResponsiveValue({
      mobile: 4,
      tablet: 6,
      laptop: 6,
      desktop: 8
    }),
    alignItems: 'center',
    position: 'relative',
  },
  profileImage: {
    width: getResponsiveValue({
      mobile: 60,
      tablet: 70,
      laptop: 80,
      desktop: 90
    }),
    height: getResponsiveValue({
      mobile: 60,
      tablet: 70,
      laptop: 80,
      desktop: 90
    }),
    borderRadius: getResponsiveValue({
      mobile: 30,
      tablet: 35,
      laptop: 40,
      desktop: 45
    }),
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
  },
  profileImagePlaceholder: {
    width: getResponsiveValue({
      mobile: 60,
      tablet: 70,
      laptop: 80,
      desktop: 90
    }),
    height: getResponsiveValue({
      mobile: 60,
      tablet: 70,
      laptop: 80,
      desktop: 90
    }),
    borderRadius: getResponsiveValue({
      mobile: 30,
      tablet: 35,
      laptop: 40,
      desktop: 45
    }),
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
  },
  profileImagePlaceholderText: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 24,
      tablet: 28,
      laptop: 32,
      desktop: 36
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  paperPlaneButton: {
    position: 'absolute',
    top: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    right: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    borderRadius: getResponsiveValue({
      mobile: 10,
      tablet: 12,
      laptop: 14,
      desktop: 16
    }),
    width: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    height: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    padding: getResponsiveValue({
      mobile: 2,
      tablet: 2,
      laptop: 3,
      desktop: 3
    }),
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'scale(1.1)',
        opacity: 0.9,
      }
    }),
  },
  profileFolderButton: {
    position: 'absolute',
    top: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    right: getResponsiveValue({
      mobile: 46,
      tablet: 52,
      laptop: 58,
      desktop: 58
    }),
    borderRadius: getResponsiveValue({
      mobile: 10,
      tablet: 12,
      laptop: 14,
      desktop: 16
    }),
    width: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    height: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'scale(1.1)',
        opacity: 0.9,
      }
    }),
  },
  paperPlaneIcon: {
    width: '80%',
    height: '70%',
  },
  legendContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: getResponsiveValue({
      mobile: 12,
      tablet: 16,
      laptop: 20,
      desktop: 20
    }),
    marginHorizontal: getResponsiveValue({
      mobile: 20,
      tablet: 40,
      laptop: 60,
      desktop: 80
    }),
    marginVertical: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 25,
      desktop: 30
    }),
    alignItems: 'center',
  },
  legendTitle: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 16,
      laptop: 18,
      desktop: 18
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
  },
  legendItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: getResponsiveValue({
      mobile: 12,
      tablet: 16,
      laptop: 20,
      desktop: 24
    }),
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: getResponsiveValue({
      mobile: 6,
      tablet: 8,
      laptop: 10,
      desktop: 10
    }),
  },
  legendIconContainer: {
    width: getResponsiveValue({
      mobile: 20,
      tablet: 24,
      laptop: 28,
      desktop: 28
    }),
    height: getResponsiveValue({
      mobile: 20,
      tablet: 24,
      laptop: 28,
      desktop: 28
    }),
    borderRadius: getResponsiveValue({
      mobile: 10,
      tablet: 12,
      laptop: 14,
      desktop: 14
    }),
    padding: getResponsiveValue({
      mobile: 4,
      tablet: 5,
      laptop: 6,
      desktop: 6
    }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  legendIcon: {
    width: '100%',
    height: '100%',
  },
  legendText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 13,
      laptop: 14,
      desktop: 14
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
  },
  seeLegendButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: getResponsiveValue({
      mobile: 10,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    paddingVertical: getResponsiveValue({
      mobile: 3,
      tablet: 4,
      laptop: 5,
      desktop: 5
    }),
    borderRadius: getResponsiveValue({
      mobile: 15,
      tablet: 18,
      laptop: 20,
      desktop: 20
    }),
    marginTop: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        transform: 'scale(1.05)',
      }
    }),
  },
  seeLegendText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: getResponsiveValue({
      mobile: 20,
      tablet: 40,
      laptop: 60,
      desktop: 80
    }),
  },
  folderSelectModalContent: {
    borderRadius: 12,
    width: '100%',
    maxWidth: 900,
    height: '100%',
    maxHeight: 1000,
    overflow: 'hidden',
  },
  legendModal: {
    backgroundColor: '#3D7FE6',
    borderRadius: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    width: '100%',
    maxWidth: getResponsiveValue({
      mobile: 320,
      tablet: 400,
      laptop: 480,
      desktop: 520
    }),
    maxHeight: '80%',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
    }),
  },
  legendHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.2)',
  },
  legendModalTitle: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 18,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
  },
  closeButton: {
    width: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    height: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    borderRadius: getResponsiveValue({
      mobile: 16,
      tablet: 18,
      laptop: 20,
      desktop: 20
    }),
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        transform: 'scale(1.1)',
      }
    }),
  },
  closeButtonText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 20,
      tablet: 22,
      laptop: 24,
      desktop: 24
    }),
    fontWeight: 'bold',
  },
  legendModalContent: {
    padding: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
  },
  legendModalItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    gap: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
  },
  legendModalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    minWidth: getResponsiveValue({
      mobile: 120,
      tablet: 140,
      laptop: 160,
      desktop: 160
    }),
  },
  legendModalText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 16,
      laptop: 18,
      desktop: 18
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  legendIcon: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  legendText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 13,
      laptop: 14,
      desktop: 14
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
  },
  seeLegendButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: getResponsiveValue({
      mobile: 10,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    paddingVertical: getResponsiveValue({
      mobile: 3,
      tablet: 4,
      laptop: 5,
      desktop: 5
    }),
    borderRadius: getResponsiveValue({
      mobile: 15,
      tablet: 18,
      laptop: 20,
      desktop: 20
    }),
    marginTop: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        transform: 'scale(1.05)',
      }
    }),
  },
  seeLegendText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: getResponsiveValue({
      mobile: 20,
      tablet: 40,
      laptop: 60,
      desktop: 80
    }),
  },
  legendModal: {
    backgroundColor: '#3D7FE6',
    borderRadius: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    width: '100%',
    maxWidth: getResponsiveValue({
      mobile: 320,
      tablet: 400,
      laptop: 480,
      desktop: 520
    }),
    maxHeight: '80%',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
    }),
  },
  legendHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.2)',
  },
  legendModalTitle: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 18,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
  },
  closeButton: {
    width: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    height: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    borderRadius: getResponsiveValue({
      mobile: 16,
      tablet: 18,
      laptop: 20,
      desktop: 20
    }),
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        transform: 'scale(1.1)',
      }
    }),
  },
  closeButtonText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 20,
      tablet: 22,
      laptop: 24,
      desktop: 24
    }),
    fontWeight: 'bold',
  },
  legendModalContent: {
    padding: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
  },
  legendModalItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    gap: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
  },
  legendModalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    minWidth: getResponsiveValue({
      mobile: 120,
      tablet: 140,
      laptop: 160,
      desktop: 160
    }),
  },
  legendModalText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 16,
      laptop: 18,
      desktop: 18
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  verificationText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 14,
      laptop: 16,
      desktop: 18
    }),
    fontWeight: 'bold',
  },
  profileName: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 16,
      tablet: 17,
      laptop: 18,
      desktop: 20
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: getResponsiveValue({
      mobile: 2,
      tablet: 3,
      laptop: 4,
      desktop: 5
    }),
  },
  userTypeText: {
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 13,
      laptop: 14,
      desktop: 15
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: getResponsiveValue({
      mobile: 2,
      tablet: 3,
      laptop: 4,
      desktop: 5
    }),
  },
  verificationStatus: {
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 12,
      desktop: 13
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: getResponsiveValue({
      mobile: 4,
      tablet: 6,
      laptop: 8,
      desktop: 10
    }),
  },
  locationText: {
    color: 'rgba(0, 0, 0, 0.7)',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 12,
      desktop: 13
    }),
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },
  titleText: {
    color: '#578dff',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 12,
      desktop: 13
    }),
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
  },
  bioText: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 12,
      desktop: 13
    }),
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
    lineHeight: getResponsiveValue({
      mobile: 14,
      tablet: 15,
      laptop: 16,
      desktop: 18
    }),
    paddingHorizontal: getResponsiveValue({
      mobile: 6,
      tablet: 8,
      laptop: 10,
      desktop: 12
    }),
  },
  postGridSummary: {
    marginTop: 6,
    fontSize: 11,
    color: 'rgba(0, 0, 0, 0.6)',
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },
  postCard: {
    backgroundColor: '#feffe4',
    borderRadius: 12,
    overflow: 'visible',
    padding: 20,
    alignSelf: 'stretch',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
  },
  postCardBgLayer: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 12,
    overflow: 'hidden',
    zIndex: 0,
  },
  postCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
    paddingBottom: 16,
    zIndex: 10,
    width: '100%',
  },
  postCardHeaderInputs: {
    flex: 1,
  },
  postCardActions: {
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
  },
  postCardOptionsBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
    paddingHorizontal: 4,
    paddingVertical: 4,
    position: 'relative',
    zIndex: 31,
  },
  postCardOptionsMenu: {
    position: 'absolute',
    top: 28,
    right: 0,
    backgroundColor: '#ffffff',
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 12,
    minWidth: 120,
    zIndex: 50,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 12px rgba(0,0,0,0.18)' } : {}),
  },
  postCardOptionsItem: {
    paddingVertical: 6,
  },
  postCardOptionsItemText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: '#333',
  },
  postCardPostInput: {
    fontFamily: 'Computer Says No',
    fontSize: 28,
    color: '#000',
    paddingVertical: 2,
  },
  postCardLocationInput: {
    fontFamily: 'Computer Says No',
    fontSize: 20,
    color: '#333',
    paddingVertical: 2,
  },
  postCardFolderBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
  },
  postCardAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  postCardAvatarPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  postCardAvatarText: {
    fontFamily: 'Glacial Indifference',
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  postCardHeaderText: {
    flex: 1,
    marginLeft: 10,
  },
  postCardAuthor: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '600',
    color: '#000',
  },
  postCardSummary: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 11,
    color: 'rgba(0,0,0,0.65)',
    marginTop: 2,
  },
  socialLinksContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 14
    }),
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 14
    }),
    marginHorizontal: getResponsiveValue({
      mobile: -3,
      tablet: -4,
      laptop: -5,
      desktop: -6
    }),
  },
  socialLinkButton: {
    backgroundColor: '#3b82f6',
    paddingHorizontal: getResponsiveValue({
      mobile: 10,
      tablet: 12,
      laptop: 14,
      desktop: 16
    }),
    paddingVertical: getResponsiveValue({
      mobile: 5,
      tablet: 6,
      laptop: 7,
      desktop: 8
    }),
    borderRadius: 16,
    marginHorizontal: getResponsiveValue({
      mobile: 3,
      tablet: 4,
      laptop: 5,
      desktop: 6
    }),
    marginVertical: getResponsiveValue({
      mobile: 3,
      tablet: 4,
      laptop: 5,
      desktop: 6
    }),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  socialLinkButtonText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 12,
      desktop: 13
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  memberStats: {
    position: 'absolute',
    bottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    width: '100%',
    alignItems: 'center',
  },
  memberStat: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 11,
      desktop: 12
    }),
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },

  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    minHeight: 100,
  },
  loadingText: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    marginTop: 15,
    textAlign: 'center',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    minHeight: 100,
  },
  emptyText: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: 18,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: 10,
    textAlign: 'center',
  },
  emptySubtext: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },
  resultsHeader: {
    padding: 16,
  },
  resultsCount: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  enhancedSearchBadge: {
    backgroundColor: 'rgba(78, 125, 224, 0.2)',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  enhancedSearchText: {
    color: '#4e7de0',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  loadingMoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  loadingMoreText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    marginLeft: 10,
  },
  endContainer: {
    alignItems: 'center',
    padding: 20,
  },
  endText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
  },
  publicMediaContainer: {
    width: '100%',
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
  },
  publicMediaTitle: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 11,
      tablet: 12,
      laptop: 13,
      desktop: 14
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: getResponsiveValue({
      mobile: 4,
      tablet: 5,
      laptop: 6,
      desktop: 8
    }),
  },
  mediaItemsContainer: {
    gap: getResponsiveValue({
      mobile: 2,
      tablet: 3,
      laptop: 4,
      desktop: 5
    }),
  },
  mediaPreviewGrid: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: getResponsiveValue({
      mobile: 4,
      tablet: 6,
      laptop: 8,
      desktop: 10
    }),
  },
  mediaPreviewItem: {
    width: '30%',
    aspectRatio: 3/2,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    overflow: 'hidden',
    position: 'relative',
  },
  mediaPreviewContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  mediaOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoDurationOverlay: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
  },
  durationText: {
    color: '#ffffff',
    fontSize: 8,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  mediaPreviewImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  mediaPreviewIcon: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    flexDirection: 'column',
  },
  mediaPreviewLabel: {
    color: '#ffffff',
    fontSize: 8,
    fontFamily: 'VastagoGrotesk',
    marginTop: 2,
    fontWeight: 'bold',
    textAlign: 'center',
    numberOfLines: 1,
    ellipsizeMode: 'middle',
  },
  audioWaveformPreview: {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 4,
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  audioWaveformContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    width: '100%',
  },
  audioWaveformBar: {
    width: 2,
    backgroundColor: '#aaa',
    marginHorizontal: 1,
    borderRadius: 1,
    minHeight: 8,
  },
  audioPlayIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 12,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaItem: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 4,
    padding: getResponsiveValue({
      mobile: 4,
      tablet: 5,
      laptop: 6,
      desktop: 8
    }),
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  mediaItemName: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 11,
      desktop: 12
    }),
    fontFamily: 'VastagoGrotesk',
  },
  moreMediaText: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: getResponsiveValue({
      mobile: 8,
      tablet: 9,
      laptop: 10,
      desktop: 11
    }),
    fontFamily: 'VastagoGrotesk',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: getResponsiveValue({
      mobile: 2,
      tablet: 3,
      laptop: 4,
      desktop: 5
    }),
  },
  organizationBadge: {
    position: 'absolute',
    top: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    left: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    paddingHorizontal: getResponsiveValue({
      mobile: 6,
      tablet: 8,
      laptop: 10,
      desktop: 12
    }),
    paddingVertical: getResponsiveValue({
      mobile: 2,
      tablet: 3,
      laptop: 4,
      desktop: 5
    }),
    borderRadius: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 14
    }),
    zIndex: 999,
  },
  authModalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  authModalContent: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  authModalTitle: {
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 18,
      laptop: 20,
      desktop: 22
    }),
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
    color: '#424b54',
    marginBottom: 20,
    textAlign: 'center',
  },
  authModalMessage: {
    fontSize: getResponsiveValue({
      mobile: 16,
      tablet: 17,
      laptop: 18,
      desktop: 20
    }),
    marginBottom: 10,
    fontFamily: 'Glacial Indifference',
    color: '#424b54',
    textAlign: 'center',
    lineHeight: 24,
  },
  organizationBadgeText: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 11,
      desktop: 12
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  postModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  postModalContent: {
    backgroundColor: '#ffffff',
    borderRadius: 25,
    paddingVertical: 12,
    paddingHorizontal: 28,
    width: '100%',
    maxWidth: 700,
    maxHeight: '90%',
  },
  postModalContentDirectory: {
    ...(Platform.OS === 'web' && {
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '90vh',
    }),
  },
  postModalDirectoryScroll: {
    ...(Platform.OS === 'web' ? { maxHeight: '58vh' } : { maxHeight: 420 }),
    width: '100%',
  },
  postModalDirectoryScrollContent: {
    flexGrow: 1,
    paddingBottom: 8,
  },
  postModalTitle: {
    fontSize: 32,
    fontFamily: 'Playpen Sans',
    color: '#fbcd9d',
    textAlign: 'center',
    marginBottom: 18,
  },
  postModalSubtitle: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: '#166fff',
    textAlign: 'center',
    marginBottom: 24,
  },
  postModalCategoryFont: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
  },
  postAsRow: {
    marginBottom: 16,
  },
  postAsLabel: {
    fontFamily: 'Glacial Indifference',
    fontSize: 14,
    color: '#333',
    marginBottom: 8,
    textAlign: 'center',
  },
  postAsChipsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  postAsChip: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#ccc',
    backgroundColor: '#fff',
    marginRight: 8,
  },
  postAsChipSelected: {
    backgroundColor: '#166fff',
    borderColor: '#166fff',
  },
  postAsChipText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 12,
    color: '#333',
    textAlign: 'center',
  },
  postAsChipTextSelected: {
    color: '#fff',
  },
  postAsPersonaCard: {
    width: 120,
    minHeight: 136,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  postAsChipAvatarWrap: {
    width: 52,
    height: 52,
    borderRadius: 26,
    overflow: 'hidden',
    marginBottom: 8,
    backgroundColor: '#e8e8e8',
  },
  postAsChipAvatarImage: {
    width: '100%',
    height: '100%',
  },
  postAsChipAvatarPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  postAsChipLabelBlock: {
    width: '100%',
    minHeight: 34,
    maxHeight: 34,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 2,
  },
  postAsChipFooter: {
    width: '100%',
    height: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  postAsChipFooterSpacer: {
    height: 14,
    width: 1,
    opacity: 0,
  },
  postAsChipAvatarInitial: {
    fontSize: 18,
    fontWeight: '700',
    color: '#555',
    fontFamily: 'VastagoGrotesk',
  },
  postAsChipDisabled: {
    opacity: 0.45,
  },
  postAsChipLockedLabel: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 9,
    color: '#888',
    marginTop: 2,
    textAlign: 'center',
  },
  postAsCreateRow: {
    gap: 8,
    marginBottom: 10,
  },
  postAsCreateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: 'rgba(22,111,255,0.35)',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: 'rgba(22,111,255,0.06)',
  },
  postAsCreateBtnText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 13,
    color: '#166fff',
  },
  postAsOrgEditorModal: {
    maxWidth: 920,
    width: '100%',
    maxHeight: '92%',
  },
  postAsOrgEditorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  postAsOrgEditorCloseBtn: {
    padding: 6,
  },
  postAsOrgEditorScroll: {
    maxHeight: 620,
  },
  postAsOrgEditorEmptyText: {
    textAlign: 'center',
    color: '#666',
    fontFamily: 'VastagoGrotesk',
    paddingVertical: 20,
  },
  postModalColumns: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 20,
    marginBottom: 24,
  },
  postModalColumn: {
    flex: 1,
    minWidth: 140,
  },
  postModalColumnHeader: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: '700',
    color: '#000000',
    marginBottom: 12,
  },
  postModalOption: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 13,
    color: '#000000',
    paddingVertical: 4,
    marginBottom: 2,
  },
  postModalOptionSelected: {
    textDecorationLine: 'underline',
  },
  postModalTextInput: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 13,
    color: '#000000',
    paddingVertical: 6,
    marginTop: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
    minWidth: 40,
  },
  postModalTextInputEmpty: {
    opacity: 0.5,
  },
  postModalTextInputFilled: {
    opacity: 1,
  },
  postModalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 8,
  },
  postModalContinueBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  postModalContinueText: {
    fontFamily: 'Playpen Sans',
    fontSize: 16,
    fontWeight: '600',
    color: '#166fff',
  },
  postModalContinuePostFont: {
    fontFamily: 'Playpen Sans',
  },
});

export default CommunityList;
